<template>
  <li class="nav-item" v-if="accessAllowed">
    <nav-link
      v-slot="{ href, navigate }"
      :to="to"
      custom
    >
      <a
        :href="href"
        :class="{ active, open }"
        class="link"
        @click="navigate"
      >
        <div v-if="icon" class="h-full flex justify-center min-w-[2.25rem]">
          <component :is="icon" class="h-full" />
        </div>
        <span>
          {{ label }}
        </span>
        <span v-if="$slots.default" class="chevron" @click.prevent="toggle">
          <chevron-icon />
        </span>
      </a>
    </nav-link>
    <collapsable v-if="$slots.default" :open="open" class="collapseable">
      <slot />
    </collapsable>
  </li>
</template>

<script lang="ts" setup>
import {
  unref, ref, watch, toRef, inject, provide, reactive, computed,
} from 'vue';
import type { RouterLinkProps } from 'vue-router';
import { useLink } from 'vue-router';
import ChevronIcon from '@images/chevron.svg?component';
import NavLink from '@components/structure/navigation/NavLink.vue';
import Collapsable from '@/components/utils/Collapsable.vue';
import { useLocation } from '@/hooks/location';
import { syncRef } from '@/hooks/state';
import { activeTrackerKey } from './injects';
import { useStore } from '@/store';
import { canAccess } from '@/utils/user';

interface Props {
  icon?: object;
  label?: string;
  to: RouterLinkProps['to'];
}

interface ActiveTracker {
  counter: number;
}

const store = useStore();
const user = computed(() => store.state.auth.user);

const props = defineProps<Props>();
const toLocation = useLocation(toRef(props, 'to'));

const link = useLink({ to: toLocation });
const active = syncRef(link.isActive);

const activeTracker: ActiveTracker = reactive({ counter: active.value ? 1 : 0 });

provide(activeTrackerKey, activeTracker);

const parentTracker = inject(activeTrackerKey, null);

const open = ref(false);

const accessAllowed = computed(() => {
  const allowedRoles = link.route.value.meta.roles as string[];
  if (!allowedRoles) return true;
  return canAccess(user.value, allowedRoles);
});

watch(activeTracker, (newTracker) => {
  if (newTracker.counter === 0) {
    active.value = false;
  } else {
    active.value = true;
  }
});

watch(active, (newActive) => {
  if (parentTracker) {
    if (newActive) {
      parentTracker.counter += 1;
    } else {
      parentTracker.counter -= 1;
    }
  }

  // Only auto-open when becoming active, don't auto-close
  // This prevents the menu from closing when clicking on child items
  if (newActive) {
    open.value = true;
  }
});

const toggle = () => {
  open.value = !unref(open);
};
</script>

<style lang="scss" scoped>
.nav-item {
  @apply text-nav;
  @apply w-full;
  @apply text-inactive;

  transition: background var(--speed-fast) ease color var(--speed-fast) ease;

  .collapseable :deep(.inner) {
    border-bottom: 3px solid;
    @apply border-b-selected;
  }

  .link {
    @apply cursor-pointer;
    @apply w-full;
    @apply flex items-center;

    column-gap: px(20);
    padding: px(21) px(20);
    height: px(70);

    &:deep(svg path) {
      fill: currentColor;
    }

    .chevron {
      flex: 0 0 auto;
      transition: transform var(--speed-fast) ease;
      padding: px(10);
      margin-left: auto;

      :deep(svg) {
        @apply w-3 h-3;
      }
    }

    &.active {
      @apply bg-selected text-white;
    }

    &.open {
      .chevron {
        transform: rotate(180deg);
      }
    }

    &.inline {
      @apply bg-none text-current;

      &.active {
        @apply border-l-white;
        border-left: 3px;
      }
    }

    &:hover {
      @apply bg-selected text-white;
      background: rgba(var(--color-selected-rgb), 0.9);
      opacity: 0.9;
    }
  }
}
</style>
